import { forwardRef, type ButtonHTMLAttributes, type AnchorHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost';

const base = 'inline-flex items-center justify-center gap-2 font-sans font-semibold rounded-[10px] transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-l-terracotta';

const sizes = 'h-11 px-6 text-[15px]';

const variants: Record<Variant, string> = {
  primary: 'bg-l-terracotta text-white hover:bg-l-marginalia',
  secondary: 'border border-l-ink text-l-ink bg-transparent hover:bg-l-paper-soft',
  ghost: 'text-l-ink-muted hover:text-l-ink',
};

type ButtonProps = {
  variant?: Variant;
  className?: string;
  asChild?: never;
  href?: never;
} & ButtonHTMLAttributes<HTMLButtonElement>;

type AnchorProps = {
  variant?: Variant;
  className?: string;
  href: string;
  asChild?: never;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

type Props = ButtonProps | AnchorProps;

export const LButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  ({ variant = 'primary', className, ...rest }, ref) => {
    const classes = cn(base, sizes, variants[variant], className);
    if ('href' in rest && rest.href !== undefined) {
      return <a ref={ref as React.Ref<HTMLAnchorElement>} className={classes} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)} />;
    }
    return <button ref={ref as React.Ref<HTMLButtonElement>} className={classes} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)} />;
  },
);
LButton.displayName = 'LButton';
